import React, { useState, useEffect } from 'react';
import {
  FaLightbulb,
  FaUser,
  FaTimes,
  FaChevronRight,
  FaCrown,
  FaCalendarAlt,
  FaEnvelope,
  FaStethoscope,
  FaCogs
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { isIOS } from '../utils/platform';

const DailyCard = ({ 
  currentTip, 
  userProfile, 
  memberSinceText,
  onHideTip,
  onNextTip,
  isCapacitor = false
}) => {
  const [hoveredSection, setHoveredSection] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [hoveredPlanButton, setHoveredPlanButton] = useState(false);
  const navigate = useNavigate();

  const isEnterprisePlan = (() => {
    const plan = String(userProfile?.subscription?.planType || '').toLowerCase();
    return plan === 'enterprise' || plan === 'entreprise';
  })();

  const getPlanColors = (planTypeRaw) => {
    const plan = (planTypeRaw || '').toString().toLowerCase();
    if (plan === 'enterprise' || plan === 'entreprise') {
      return {
        start: '#8b5cf6', // violet start
        end: '#7c3aed',   // violet end
        border: '#7c3aed',
        text: '#7c3aed',
        shadow: 'rgba(124,58,237,0.3)'
      };
    }
    if (plan === 'silver') {
      return {
        start: '#9ca3af', // gray
        end: '#6b7280',   // gray dark
        border: '#6b7280',
        text: '#6b7280',
        shadow: 'rgba(107,114,128,0.3)'
      };
    }
    if (plan === 'bronze') {
      return {
        start: '#cd7f32', // bronze
        end: '#b45309',   // copper/dark orange
        border: '#b45309',
        text: '#b45309',
        shadow: 'rgba(180,83,9,0.3)'
      };
    }
    // default gold
    return {
      start: '#fbbf24',
      end: '#f59e0b',
      border: '#fbbf24',
      text: '#f59e0b',
      shadow: 'rgba(251,191,36,0.3)'
    };
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: isCapacitor ? '12px' : '20px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f0f0f0',
      cursor: 'default'
    },
    // Desktop Account Section
    accountSection: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '16px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#e5e7eb',
      transition: 'all 0.3s ease',
      position: 'relative',
      cursor: 'pointer'
    },
    accountSectionHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.1)',
      borderColor: '#ddd6fe'
    },
    accountHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    accountLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flex: 1
    },
    avatarWrapper: {
      width: '56px',
      height: '56px',
      background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
      flexShrink: 0
    },
    avatarIcon: {
      fontSize: '24px',
      color: 'white'
    },
    accountInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    accountName: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1f2937',
      letterSpacing: '-0.5px'
    },
    accountDetails: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap'
    },
    accountDetail: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: '#6b7280'
    },
    planContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    planBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: 'white',
      padding: '4px 10px',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
      boxShadow: '0 2px 8px rgba(251, 191, 36, 0.3)'
    },
    planButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '8px',
      background: 'white',
      border: '1px solid #fbbf24',
      color: '#f59e0b',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    planButtonHover: {
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: 'white',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(251, 191, 36, 0.3)'
    },
    // Tip Section Styles
    tipSection: {
      position: 'relative',
      padding: '14px',
      borderRadius: '10px',
      background: 'linear-gradient(135deg, #fff8e1, #fff3e0)',
      border: '1px solid #ffecb3',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer'
    },
    tipSectionHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(251, 191, 36, 0.2)'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '10px'
    },
    iconWrapper: {
      width: '28px',
      height: '28px',
      borderRadius: '6px',
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      color: 'white',
      flexShrink: 0
    },
    sectionTitle: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#f59e0b',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      flex: 1
    },
    closeButton: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: 'white',
      border: '1px solid #fbbf24',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '10px',
      color: '#f59e0b',
      opacity: 0,
      transition: 'opacity 0.2s ease',
      zIndex: 1
    },
    sectionContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    contentTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '4px',
      lineHeight: '1.3'
    },
    contentText: {
      fontSize: '14px',
      color: '#6b7280',
      lineHeight: '1.5',
      margin: 0
    },
    actionButton: {
      fontSize: '12px',
      padding: '4px 10px',
      borderRadius: '6px',
      border: '1px solid #fbbf24',
      background: 'white',
      color: '#f59e0b',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontWeight: '500'
    },
    actionButtonHover: {
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: 'white',
      transform: 'scale(1.02)'
    }
  };

  // Mobile Compact Version - Account + Tip
  if (isCapacitor) {
    return (
      <div
        style={styles.container}
      >
        {/* Compact Account Section */}
        <div onClick={isEnterprisePlan || isIOS ? undefined : () => navigate('/settings/subscription')} style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '12px',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: isEnterprisePlan || isIOS ? 'default' : 'pointer'
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <FaUser style={{ fontSize: '20px', color: 'white' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {userProfile?.lastNameAndTitle || 'Utilisateur'}
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              {userProfile?.subscription && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {(() => {
                    const { start, end } = getPlanColors(userProfile.subscription.planType);
                    return (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: `linear-gradient(135deg, ${start}, ${end})`,
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        <FaCrown style={{ fontSize: '9px' }} />
                        {isIOS && userProfile.subscription.planType.toLowerCase() === 'silver'
                          ? 'MOBILE'
                          : userProfile.subscription.planType.toUpperCase()}
                      </div>
                    );
                  })()}
                  {!isEnterprisePlan && !isIOS && (
                  <button
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      background: 'white',
                      border: `1px solid ${getPlanColors(userProfile.subscription.planType).border}`,
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/settings/subscription');
                    }}
                  >
                    <FaChevronRight style={{ fontSize: '10px', color: getPlanColors(userProfile.subscription.planType).text }} />
                  </button>
                  )}
                </div>
              )}
              {userProfile?.specialty && (
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px'
                }}>
                  <FaStethoscope style={{ fontSize: '9px' }} />
                  <span>{userProfile.specialty.charAt(0).toUpperCase() + userProfile.specialty.slice(1)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tip Section */}
        {currentTip && (
          <div onClick={() => onNextTip?.()} style={{
            padding: '12px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #fff8e1, #fff3e0)',
            border: '1px solid #ffecb3',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <FaLightbulb style={{ color: 'white', fontSize: '16px' }} />
              </div>
              <span style={{
                fontSize: '13px',
                fontWeight: '700',
                color: '#f59e0b',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                flex: 1
              }}>
                Astuce du jour
              </span>
              <button
                style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  background: 'white',
                  border: '1px solid #fbbf24',
                  color: '#f59e0b',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginRight: '6px'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onNextTip?.();
                }}
              >
                Suivante →
              </button>
              <button
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  background: 'white',
                  border: '1px solid #fbbf24',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onHideTip?.();
                }}
              >
                <FaTimes style={{ fontSize: '11px', color: '#f59e0b' }} />
              </button>
            </div>
            <div>
              <h4 style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '6px',
                lineHeight: '1.3'
              }}>
                {currentTip.title}
              </h4>
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                lineHeight: '1.5'
              }}>
                {currentTip.text}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop Version
  return (
    <div
      style={styles.container}
    >
      {/* Account Section - Full Width Top */}
      <div
        style={{
          ...styles.accountSection,
          ...(hoveredSection === 'account' ? styles.accountSectionHover : {}),
          cursor: isEnterprisePlan || isIOS ? 'default' : 'pointer'
        }}
        onClick={isEnterprisePlan || isIOS ? undefined : () => navigate('/settings/subscription')}
        onMouseEnter={isEnterprisePlan || isIOS ? undefined : () => setHoveredSection('account')}
        onMouseLeave={isEnterprisePlan || isIOS ? undefined : () => setHoveredSection(null)}
      >
        <div style={styles.accountHeader}>
          <div style={styles.accountLeft}>
            <div style={styles.avatarWrapper}>
              <FaUser style={styles.avatarIcon} />
            </div>
            <div style={styles.accountInfo}>
              <div style={styles.accountName}>
                {userProfile?.lastNameAndTitle || 'Utilisateur'}
              </div>
              <div style={styles.accountDetails}>
                {userProfile?.subscription && (
                  <div style={styles.planContainer}>
                    {(() => { const { start, end, border, text, shadow } = getPlanColors(userProfile.subscription.planType); return (
                      <div style={{
                        ...styles.planBadge,
                        background: `linear-gradient(135deg, ${start}, ${end})`,
                        boxShadow: `0 2px 8px ${shadow}`
                      }}>
                        <FaCrown style={{ fontSize: '11px' }} />
                        Plan {isIOS && userProfile.subscription.planType.toLowerCase() === 'silver'
                          ? 'MOBILE'
                          : userProfile.subscription.planType.toUpperCase()}
                      </div>
                    ); })()}
                    {!isEnterprisePlan && !isIOS && (
                      <button
                        style={{
                          ...styles.planButton,
                          border: `1px solid ${getPlanColors(userProfile.subscription.planType).border}`,
                          color: getPlanColors(userProfile.subscription.planType).text,
                          ...(hoveredPlanButton ? {
                            background: `linear-gradient(135deg, ${getPlanColors(userProfile.subscription.planType).start}, ${getPlanColors(userProfile.subscription.planType).end})`,
                            color: 'white',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${getPlanColors(userProfile.subscription.planType).shadow}`
                          } : {})
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/settings/subscription');
                        }}
                        onMouseEnter={() => setHoveredPlanButton(true)}
                        onMouseLeave={() => setHoveredPlanButton(false)}
                      >
                        <FaCogs style={{ fontSize: '10px' }} />
                        Gérer
                      </button>
                    )}
                  </div>
                )}
                {userProfile?.specialty && (
                  <div style={styles.accountDetail}>
                    <FaStethoscope style={{ fontSize: '11px' }} />
                    <span>{userProfile.specialty.charAt(0).toUpperCase() + userProfile.specialty.slice(1)}</span>
                  </div>
                )}
                {userProfile?.user?.email && (
                  <div style={styles.accountDetail}>
                    <FaEnvelope style={{ fontSize: '11px' }} />
                    <span>{userProfile.user.email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tip Section - Full Width */}
      {currentTip && (
        <div 
          onClick={() => onNextTip?.()}
          style={{
            ...styles.tipSection,
            ...(hoveredSection === 'tip' ? styles.tipSectionHover : {})
          }}
          onMouseEnter={() => setHoveredSection('tip')}
          onMouseLeave={() => setHoveredSection(null)}
        >
          <div style={styles.sectionHeader}>
            <div style={styles.iconWrapper}>
              <FaLightbulb />
            </div>
            <span style={styles.sectionTitle}>Astuce du jour</span>
            <button 
              style={{
                ...styles.actionButton,
                marginLeft: 'auto',
                marginRight: '30px',
                ...(hoveredButton === 'next' ? styles.actionButtonHover : {})
              }}
              onClick={(e) => {
                e.stopPropagation();
                onNextTip?.();
              }}
              onMouseEnter={() => setHoveredButton('next')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Suivante →
            </button>
          </div>
          <div 
            style={{
              ...styles.closeButton,
              ...(hoveredSection === 'tip' ? { opacity: 1 } : {})
            }}
            onClick={(e) => {
              e.stopPropagation();
              onHideTip?.();
            }}
          >
            <FaTimes />
          </div>
          <div style={styles.sectionContent}>
            <h4 style={styles.contentTitle}>{currentTip.title}</h4>
            <p style={styles.contentText}>{currentTip.text}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyCard;